import { Menu, Tooltip } from '@mantine/core';
import { Languages } from 'lucide-react';
import { LANGUAGES, type LanguageCode } from '@constants/translations';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { haptic, HAPTIC } from '@utils/haptic';

interface LanguageSwitcherProps {
  /** Compact (icon-only) layout for the minimized sidebar. */
  isMinimized?: boolean;
  /** Menu position relative to the target. */
  position?: 'right' | 'top-end';
}

export default function LanguageSwitcher({ isMinimized = false, position = 'top-end' }: LanguageSwitcherProps) {
  const { language, setLanguage, meta, t } = useLanguage();
  const { colors: themeColors } = useTheme();

  const setLocale = (code: LanguageCode) => {
    setLanguage(code);
  };

  const currentLabel = t('language.switcher');

  const btnClass = isMinimized
    ? 'w-full flex items-center justify-center p-2 rounded-lg transition-colors duration-150 relative'
    : 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-150 relative';

  const applyHover = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = 'rgba(128, 128, 128, 0.12)';
  };
  const clearHover = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  return (
    <Menu position={position} offset={8}>
      <Menu.Target>
        <Tooltip label={currentLabel} position="right" disabled={!isMinimized} withArrow>
          <button
            type="button"
            onClickCapture={() => haptic(HAPTIC.medium)}
            className={btnClass}
            style={{ color: themeColors.text, backgroundColor: 'transparent' }}
            onMouseEnter={applyHover}
            onMouseLeave={clearHover}
          >
            <span className="text-[16px] shrink-0 leading-none">{meta.flag}</span>
            {!isMinimized && <span className="text-[13px] truncate flex-1 text-left">{currentLabel}</span>}
            <Languages size={14} strokeWidth={1.5} className="shrink-0" style={{ color: themeColors.textSecondary }} />
          </button>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown
        style={{
          backgroundColor: themeColors.card,
          border: `1px solid ${themeColors.border}`,
          borderRadius: 8,
          padding: 6,
        }}
      >
        {LANGUAGES.map((lang) => (
          <Menu.Item
            key={lang.code}
            leftSection={<span style={{ fontSize: 16 }}>{lang.flag}</span>}
            onClick={() => setLocale(lang.code)}
            style={{
              color: themeColors.text,
              backgroundColor: language === lang.code ? `${themeColors.accent}22` : 'transparent',
              borderRadius: 8,
              fontWeight: language === lang.code ? 600 : 500,
              padding: '8px 10px',
              fontSize: 13,
            }}
          >
            {lang.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
